// "Nearby transit" section on the unit page. Renders the nearest
// stations grouped by station name, so all the lines that stop at a
// Times Sq / Fulton St complex show up on one row instead of one row
// per platform.
//
// Source of data, in order:
//   1. `listing.building.nearbyTransit` — precomputed on the Building
//      row and already in this page's payload, so the rows render on
//      the SERVER and are present in the initial HTML.
//   2. GET /building/transit/<slug>/ — client-side fallback, reached
//      only when (1) is null: buildings that predate the backfill, or
//      have no lat/lon.
//
// Loading: 3 skeleton rows, so the section's vertical position is
// stable. Only reachable via the fallback path.
// Empty / error: returns null — section silently disappears, no
// header, no placeholder.

'use client';

import { useEffect, useState } from 'react';
import type { Listing, NearbyTransitStation } from '@repo/api';
import { getNearbyTransit } from '@repo/api';
import {
  SUBWAY_COLORS,
  SUBWAY_DARK_TEXT_LINES,
  groupNearbyTransit,
  type GroupedTransitStation,
} from '@repo/utils';
import { Divider, SectionContainer, SectionHeader, Skeleton } from '@/components/ui';

interface NearbyTransitProps {
  listing: Listing;
}

export function NearbyTransit({ listing }: NearbyTransitProps) {
  // null  → loading (show skeleton)
  // []    → loaded, empty (hide section)
  // [...] → loaded, populated (render rows)
  //
  // Seeded from the building the page payload already carries, so the station
  // rows are present in the SERVER-RENDERED HTML. Starting at `null` and
  // fetching on mount meant the only thing a crawler ever saw under the
  // "Nearby transit" heading was three aria-hidden skeleton rows — the
  // heading was indexable, the content was not.
  //
  // `nearbyTransit` is precomputed on the Building row, so the common case
  // needs no request at all. It is null only on buildings that predate the
  // backfill (or have no lat/lon); those still fall through to the endpoint
  // below, which is why the fetch is kept rather than deleted.
  const seeded = listing.building?.nearbyTransit ?? null;
  const [stations, setStations] = useState<NearbyTransitStation[] | null>(seeded);
  const hasSeeded = seeded !== null;

  useEffect(() => {
    if (hasSeeded) return;
    if (!listing.buildingSlug) {
      setStations([]);
      return;
    }
    let cancelled = false;
    getNearbyTransit(listing.buildingSlug)
      .then(res => {
        if (cancelled) return;
        setStations(res.nearbyTransit ?? []);
      })
      .catch(() => {
        if (!cancelled) setStations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [listing.buildingSlug, hasSeeded]);

  // Loading — render the header + 3 skeleton rows so vertical layout
  // is stable while the fetch completes. ~50ms in dev, faster in
  // prod after Postgres warms up.
  if (stations === null) {
    return <NearbyTransitSkeleton />;
  }

  // Loaded but empty (no stations within radius, or fetch errored).
  // Don't show the section at all — no header, no placeholder.
  if (stations.length === 0) return null;

  const grouped = groupNearbyTransit(stations);

  return (
    <SectionContainer>
      <SectionHeader title="Nearby transit" className="w-full items-center md:items-start" />
      <div className="w-full md:px-2 px-0">
        <div className="flex flex-col gap-1">
          {grouped.map((station, idx) => (
            <div key={station.key} className="w-full">
              <StationRow station={station} />
              {idx < grouped.length - 1 && (
                <div className="px-2 py-0.5">
                  <Divider variant="tertiary" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}

function NearbyTransitSkeleton() {
  return (
    <SectionContainer>
      <SectionHeader title="Nearby transit" className="w-full items-center md:items-start" />
      <div className="w-full md:px-2 px-0">
        <div className="flex flex-col gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-full">
              <div className="bg-primary rounded-lg px-2 py-3 flex items-center gap-4 w-full">
                <div className="flex items-center gap-1 shrink-0">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-4 flex-1 min-w-0 max-w-[180px]" />
                <Skeleton className="h-4 w-14 shrink-0" />
              </div>
              {i < 2 && (
                <div className="px-2 py-0.5">
                  <Divider variant="tertiary" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}

function StationRow({ station }: { station: GroupedTransitStation }) {
  return (
    <div className="bg-primary rounded-lg px-2 py-3 flex items-center justify-between gap-4 w-full">
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1">
        {station.lines.map(line => (
          <LineBullet key={line} line={line} />
        ))}
        <span className="text-body-2 text-primary truncate">at {station.name}</span>
      </div>
      <span className="text-body-2-high text-primary shrink-0">{station.distanceMi} mi</span>
    </div>
  );
}

function LineBullet({ line }: { line: string }) {
  const palette = SUBWAY_COLORS as Record<string, string>;
  const bg = palette[line] ?? '#808183';
  const textColor = SUBWAY_DARK_TEXT_LINES.has(line) ? 'text-primary' : 'text-white';
  return (
    <span
      style={{ backgroundColor: bg }}
      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-body-2-high font-bold shrink-0 ${textColor}`}
      aria-label={`Subway line ${line}`}
    >
      {line}
    </span>
  );
}
